// pages/SalariesPage.tsx

import React, { useState } from "react";
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  message,
  Row,
  Col,
  Statistic,
  Tooltip,
  Dropdown,
  Descriptions,
  Alert,
  Divider,
  Typography,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  DownOutlined,
  CalculatorOutlined,
  TeamOutlined,
  FileTextOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import dayjs from "dayjs";
import { salaryService, type Salary } from "../services/salaryService";
import type { ColumnsType } from "antd/es/table";
import { employeeService } from "../services/employeeService";
import { exportSalariesToExcel } from "../utils/excelExport";
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const SalariesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();

  const [exportLoading, setExportLoading] = useState(false);

  // ==================== STATE ====================
  const [calculateModalOpen, setCalculateModalOpen] = useState(false);
  const [bulkCalculateModalOpen, setBulkCalculateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState<Salary | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Filters
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [filters, setFilters] = useState({
    year: currentYear,
    month: currentMonth,
    status: undefined as string | undefined,
    userId: undefined as string | undefined,
    page: 1,
    limit: 10,
  });

  // ==================== QUERIES ====================

  // ດຶງລາຍການເງິນເດືອນ
  const { data: salariesData, isLoading: salariesLoading } = useQuery(
    ["salaries", filters],
    () => salaryService.getSalaries(filters),
    {
      keepPreviousData: true,
    }
  );

  // ດຶງລາຍຊື່ພະນັກງານ
  const { data: usersData } = useQuery(
    "activeUsers",
    () => employeeService.getEmployees({ status: "active", limit: 1000 }),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  console.log("usersData-->", usersData);

  // ==================== MUTATIONS ====================

  // ຄຳນວນເງິນເດືອນ 1 ຄົນ
  const calculateMutation = useMutation(salaryService.calculateSalary, {
    onSuccess: () => {
      message.success("ຄຳນວນເງິນເດືອນສຳເລັດ");
      setCalculateModalOpen(false);
      form.resetFields();
      queryClient.invalidateQueries(["salaries"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ຄຳນວນຫຼາຍຄົນ
  const bulkCalculateMutation = useMutation(salaryService.calculateBulkSalary, {
    onSuccess: (response) => {
      const { summary } = response.data;
      Modal.success({
        title: "ຄຳນວນສຳເລັດ",
        content: (
          <div>
            <p>ສຳເລັດ: {summary.success} ຄົນ</p>
            <p>ຂ້າມ: {summary.skipped || 0} ຄົນ</p>
            <p>ລົ້ມເຫລວ: {summary.failed} ຄົນ</p>
          </div>
        ),
      });
      setBulkCalculateModalOpen(false);
      form.resetFields();
      queryClient.invalidateQueries(["salaries"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ຄຳນວນທັງໝົດ
  const calculateAllMutation = useMutation(salaryService.calculateAllSalary, {
    onSuccess: (response) => {
      const { summary } = response.data;
      Modal.success({
        title: "ຄຳນວນສຳເລັດ",
        content: (
          <div>
            <p>ທັງໝົດ: {summary.total} ຄົນ</p>
            <p>ສຳເລັດ: {summary.success} ຄົນ</p>
            <p>ຂ້າມ: {summary.skipped || 0} ຄົນ</p>
            <p>ລົ້ມເຫລວ: {summary.failed} ຄົນ</p>
          </div>
        ),
      });
      queryClient.invalidateQueries(["salaries"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ແກ້ໄຂເງິນເດືອນ
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      salaryService.updateSalary(id, data),
    {
      onSuccess: () => {
        message.success("ແກ້ໄຂຂໍ້ມູນສຳເລັດ");
        setUpdateModalOpen(false);
        updateForm.resetFields();
        queryClient.invalidateQueries(["salaries"]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
      },
    }
  );

  // ອະນຸມັດ
  const approveMutation = useMutation(salaryService.approveSalary, {
    onSuccess: () => {
      message.success("ອະນຸມັດສຳເລັດ");
      queryClient.invalidateQueries(["salaries"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ອະນຸມັດຫຼາຍລາຍການ
  const approveBulkMutation = useMutation(salaryService.approveBulkSalary, {
    onSuccess: (response) => {
      const { summary } = response.data;
      message.success(
        `ອະນຸມັດສຳເລັດ ${summary.success}/${summary.total} ລາຍການ`
      );
      setSelectedRowKeys([]);
      queryClient.invalidateQueries(["salaries"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ຈ່າຍເງິນ
  const payMutation = useMutation(salaryService.markAsPaid, {
    onSuccess: () => {
      message.success("ບັນທຶກການຈ່າຍເງິນສຳເລັດ");
      queryClient.invalidateQueries(["salaries"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ຈ່າຍເງິນຫຼາຍລາຍການ
  const payBulkMutation = useMutation(salaryService.payBulkSalary, {
    onSuccess: (response) => {
      const { summary } = response.data;
      message.success(
        `ຈ່າຍເງິນສຳເລັດ ${summary.success}/${summary.total} ລາຍການ`
      );
      setSelectedRowKeys([]);
      queryClient.invalidateQueries(["salaries"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ລຶບ
  const deleteMutation = useMutation(salaryService.deleteSalary, {
    onSuccess: () => {
      message.success("ລຶບຂໍ້ມູນສຳເລັດ");
      queryClient.invalidateQueries(["salaries"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ==================== HANDLERS ====================

  const handleCalculate = (values: any) => {
    calculateMutation.mutate(values);
  };

  const handleBulkCalculate = (values: any) => {
    bulkCalculateMutation.mutate(values);
  };

  const handleCalculateAll = () => {
    Modal.confirm({
      title: "ຢືນຢັນການຄຳນວນ",
      content: `ທ່ານຕ້ອງການຄຳນວນເງິນເດືອນພະນັກງານທັງໝົດສຳລັບເດືອນ ${salaryService.formatMonthYear(
        filters.year,
        filters.month
      )} ບໍ່?`,
      okText: "ຢືນຢັນ",
      cancelText: "ຍົກເລີກ",
      onOk: () => {
        calculateAllMutation.mutate({
          year: filters.year,
          month: filters.month,
        });
      },
    });
  };

  const handleUpdate = (values: any) => {
    if (selectedSalary) {
      updateMutation.mutate({
        id: selectedSalary._id,
        data: values,
      });
    }
  };

  const handleApprove = (id: string) => {
    Modal.confirm({
      title: "ຢືນຢັນການອະນຸມັດ",
      content: "ທ່ານຕ້ອງການອະນຸມັດເງິນເດືອນນີ້ບໍ່?",
      okText: "ຢືນຢັນ",
      cancelText: "ຍົກເລີກ",
      onOk: () => approveMutation.mutate(id),
    });
  };

  const handleApproveBulk = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("ກະລຸນາເລືອກລາຍການກ່ອນ");
      return;
    }

    Modal.confirm({
      title: "ຢືນຢັນການອະນຸມັດ",
      content: `ທ່ານຕ້ອງການອະນຸມັດ ${selectedRowKeys.length} ລາຍການບໍ່?`,
      okText: "ຢືນຢັນ",
      cancelText: "ຍົກເລີກ",
      onOk: () => {
        approveBulkMutation.mutate({
          salaryIds: selectedRowKeys as string[],
        });
      },
    });
  };

  const handlePay = (id: string) => {
    Modal.confirm({
      title: "ຢືນຢັນການຈ່າຍເງິນ",
      content: "ທ່ານຕ້ອງການບັນທຶກວ່າຈ່າຍເງິນແລ້ວບໍ່?",
      okText: "ຢືນຢັນ",
      cancelText: "ຍົກເລີກ",
      onOk: () => payMutation.mutate(id),
    });
  };

  const handlePayBulk = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("ກະລຸນາເລືອກລາຍການກ່ອນ");
      return;
    }

    Modal.confirm({
      title: "ຢືນຢັນການຈ່າຍເງິນ",
      content: `ທ່ານຕ້ອງການບັນທຶກການຈ່າຍເງິນ ${selectedRowKeys.length} ລາຍການບໍ່?`,
      okText: "ຢືນຢັນ",
      cancelText: "ຍົກເລີກ",
      onOk: () => {
        payBulkMutation.mutate({
          salaryIds: selectedRowKeys as string[],
        });
      },
    });
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: "ຢືນຢັນການລຶບ",
      content: "ທ່ານຕ້ອງການລຶບຂໍ້ມູນນີ້ບໍ່?",
      okText: "ລຶບ",
      okType: "danger",
      cancelText: "ຍົກເລີກ",
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleViewDetail = (salary: Salary) => {
    setSelectedSalary(salary);
    setDetailModalOpen(true);
  };

  const handleEdit = (salary: Salary) => {
    setSelectedSalary(salary);
    updateForm.setFieldsValue({
      deduction: salary.deduction,
      specialBonus: salary.specialBonus,
      notes: salary.notes,
    });
    setUpdateModalOpen(true);
  };

  // ==================== TABLE COLUMNS ====================

  const columns: ColumnsType<Salary> = [
    {
      title: "ພະນັກງານ",
      key: "employee",
      fixed: "left",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.userId.name}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.userId.employeeId}
          </div>
        </div>
      ),
    },
    {
      title: "ເດືອນ/ປີ",
      key: "period",
      width: 120,
      render: (_, record) =>
        salaryService.formatMonthYear(record.year, record.month),
    },
    {
      title: "ມາຊ້າ",
      key: "lateDays",
      width: 100,
      align: "center",
      render: (_, record) => <div>{record.lateDays}</div>,
    },
    {
      title: "ມາຕົວຈິງ",
      key: "workDays",
      width: 100,
      align: "center",
      render: (_, record) => (
        <div>
          <span>{record.actualWorkDays}</span>
        </div>
      ),
    },
    {
      title: "ຂາດ",
      key: "absentDays",
      width: 100,
      align: "center",
      render: (_, record) => <div>{record.absentDays}</div>,
    },
    {
      title: "ຊົ່ວໂມງ",
      dataIndex: "totalWorkHours",
      key: "totalWorkHours",
      width: 100,
      align: "right",
      render: (hours) => `${hours.toFixed(2)} ຊມ`,
    },
    {
      title: "ເງິນຕາມຊົ່ວໂມງ",
      dataIndex: "salaryByHours",
      key: "salaryByHours",
      width: 130,
      align: "right",
      render: (amount) => salaryService.formatCurrency(amount),
    },
    {
      title: "ໂບນັດບໍລິການ",
      dataIndex: "serviceBonus",
      key: "serviceBonus",
      width: 130,
      align: "right",
      render: (amount) => (
        <span style={{ color: "#52c41a" }}>
          {salaryService.formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: "ໂບນັດພິເສດ",
      dataIndex: "specialBonus",
      key: "specialBonus",
      width: 120,
      align: "right",
      render: (amount) => (
        <span style={{ color: "#1890ff" }}>
          {salaryService.formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: "ເງິນຖືກຕັດ",
      dataIndex: "deduction",
      key: "deduction",
      width: 120,
      align: "right",
      render: (deduction) => (
        <span style={{ color: "#1890ff" }}>
          {salaryService.formatCurrency(deduction)}
        </span>
      ),
    },
    {
      title: "ເງິນລວມ",
      dataIndex: "totalSalary",
      key: "totalSalary",
      width: 150,
      align: "right",
      render: (amount) => (
        <span style={{ fontWeight: 600, fontSize: "16px", color: "#ff4d4f" }}>
          {salaryService.formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: "ສະຖານະ",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (status) => (
        <Tag color={salaryService.getStatusColor(status)}>
          {salaryService.formatStatus(status)}
        </Tag>
      ),
    },
    {
      title: "ໝາຍເຫດ",
      key: "notes",
      width: 100,
      align: "center",
      render: (_, record) => <div>{record.notes}</div>,
    },
    {
      title: "ການດຳເນີນງານ",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="ເບິ່ງລາຍລະອຽດ">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>

          {record.status === "draft" && (
            <>
              <Tooltip title="ແກ້ໄຂ">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>

              <Tooltip title="ອະນຸມັດ">
                <Button
                  type="link"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  style={{ color: "#52c41a" }}
                  onClick={() => handleApprove(record._id)}
                />
              </Tooltip>
            </>
          )}

          {record.status === "approved" && (
            <Tooltip title="ຈ່າຍເງິນ">
              <Button
                type="link"
                size="small"
                icon={<DollarOutlined />}
                style={{ color: "#1890ff" }}
                onClick={() => handlePay(record._id)}
              />
            </Tooltip>
          )}

          <Tooltip title="ລຶບ">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ==================== DATA ====================

  const salaries = salariesData?.data?.salaries || [];
  const pagination = salariesData?.data?.pagination;
  const users = usersData?.data?.employees || [];

  // ຄຳນວນສະຖິຕິ
  const stats = {
    totalSalaries: salaries.reduce((sum, s) => sum + s.totalSalary, 0),
    draftCount: salaries.filter((s) => s.status === "draft").length,
    approvedCount: salaries.filter((s) => s.status === "approved").length,
    paidCount: salaries.filter((s) => s.status === "paid").length,
  };

  // Row Selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    getCheckboxProps: (record: Salary) => ({
      disabled: record.status === "paid",
    }),
  };

  // ================= export to excel ==================
  const handleExportToExcel = async () => {
    setExportLoading(true);
    try {
      const dateRangeInfo = {
        month: filters.month,
        year: filters.year,
      };

      // ✅ แก้ไขตรงนี้
      if (salaries) {
        exportSalariesToExcel(salaries, dateRangeInfo);
        message.success("ສົ່ງລາຍງານອອກສຳເລັດ");
      } else {
        message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ");
    } finally {
      setExportLoading(false);
    }
  };

  // ==================== RENDER ====================
  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            ຈັດການເງິນເດືອນພະນັກງານ
          </Title>
          <Text type="secondary">
            ຄິດໄລ່ເງິນເດືອນ ແລະ ອັບເດດສະຖານະການຈ່າຍເງິນ
          </Text>
        </Col>
      </Row>
      {/* ສະຖິຕິ */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="ເງິນເດືອນທັງໝົດ"
              value={stats.totalSalaries}
              formatter={(value) => salaryService.formatCurrency(Number(value))}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="ຮ່າງ"
              value={stats.draftCount}
              suffix="ລາຍການ"
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="ອະນຸມັດແລ້ວ"
              value={stats.approvedCount}
              suffix="ລາຍການ"
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="ຈ່າຍແລ້ວ"
              value={stats.paidCount}
              suffix="ລາຍການ"
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
      </Row>

      {/* ຕາຕະລາງ */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>ລາຍການເງິນເດືອນ</span>
          </Space>
        }
        extra={
          <Space>
            {/* Filters */}
            <Select
              style={{ width: 120 }}
              placeholder="ເດືອນ"
              value={filters.month}
              onChange={(value) =>
                setFilters({ ...filters, month: value, page: 1 })
              }
            >
              {salaryService.getMonths().map((m) => (
                <Option key={m.value} value={m.value}>
                  {m.label}
                </Option>
              ))}
            </Select>

            <Select
              style={{ width: 100 }}
              placeholder="ປີ"
              value={filters.year}
              onChange={(value) =>
                setFilters({ ...filters, year: value, page: 1 })
              }
            >
              {salaryService.getYears().map((y) => (
                <Option key={y.value} value={y.value}>
                  {y.label}
                </Option>
              ))}
            </Select>

            <Select
              style={{ width: 120 }}
              placeholder="ສະຖານະ"
              allowClear
              value={filters.status}
              onChange={(value) =>
                setFilters({ ...filters, status: value, page: 1 })
              }
            >
              <Option value="draft">ຮ່າງ</Option>
              <Option value="approved">ອະນຸມັດແລ້ວ</Option>
              <Option value="paid">ຈ່າຍແລ້ວ</Option>
            </Select>

            <Dropdown
              menu={{
                items: [
                  {
                    key: "calculate-one",
                    icon: <CalculatorOutlined />,
                    label: "ຄຳນວນ 1 ຄົນ",
                    onClick: () => setCalculateModalOpen(true),
                  },
                  {
                    key: "calculate-bulk",
                    icon: <TeamOutlined />,
                    label: "ຄຳນວນຫຼາຍຄົນ",
                    onClick: () => setBulkCalculateModalOpen(true),
                  },
                  {
                    key: "calculate-all",
                    icon: <TeamOutlined />,
                    label: "ຄຳນວນທັງໝົດ",
                    onClick: handleCalculateAll,
                  },
                ],
              }}
            >
              <Button type="primary" icon={<PlusOutlined />}>
                ຄຳນວນເງິນເດືອນ <DownOutlined />
              </Button>
            </Dropdown>

            <Button
              icon={<ReloadOutlined />}
              onClick={() => queryClient.invalidateQueries(["salaries"])}
            >
              ໂຫຼດຄືນໃໝ່
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportToExcel}
              loading={exportLoading}
              style={{
                background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                border: "none",
              }}
            >
              ສົ່ງອອກ Excel
            </Button>
          </Space>
        }
      >
        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <Alert
            message={
              <Space>
                <span>ເລືອກແລ້ວ {selectedRowKeys.length} ລາຍການ</span>
                <Button size="small" onClick={handleApproveBulk}>
                  ອະນຸມັດທັງໝົດ
                </Button>
                <Button size="small" onClick={handlePayBulk}>
                  ຈ່າຍເງິນທັງໝົດ
                </Button>
                <Button size="small" onClick={() => setSelectedRowKeys([])}>
                  ຍົກເລີກ
                </Button>
              </Space>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={salaries}
          rowKey="_id"
          loading={salariesLoading}
          scroll={{ x: 1500 }}
          pagination={{
            current: pagination?.current,
            pageSize: pagination?.limit,
            total: pagination?.total,
            showSizeChanger: true,
            showTotal: (total) => `ທັງໝົດ ${total} ລາຍການ`,
            onChange: (page, pageSize) => {
              setFilters({ ...filters, page, limit: pageSize });
            },
          }}
        />
      </Card>

      {/* ==================== MODALS ==================== */}

      {/* Modal: ຄຳນວນ 1 ຄົນ */}
      <Modal
        title="ຄຳນວນເງິນເດືອນ"
        open={calculateModalOpen}
        onCancel={() => {
          setCalculateModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={calculateMutation.isLoading}
        width={500}
        cancelText={"ຍົກເລິກ"}
        okText="ຢືນຢັນ"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCalculate}
          initialValues={{
            year: currentYear,
            month: currentMonth,
          }}
        >
          <Form.Item
            label="ພະນັກງານ"
            name="userId"
            rules={[{ required: true, message: "ກະລຸນາເລືອກພະນັກງານ" }]}
          >
            <Select
              showSearch
              placeholder="ເລືອກພະນັກງານ"
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={users.map((u: any) => ({
                value: u._id,
                label: `${u.name} (${u.employeeId})`,
              }))}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="ປີ"
                name="year"
                rules={[{ required: true, message: "ກະລຸນາເລືອກປີ" }]}
              >
                <Select options={salaryService.getYears()} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="ເດືອນ"
                name="month"
                rules={[{ required: true, message: "ກະລຸນາເລືອກເດືອນ" }]}
              >
                <Select options={salaryService.getMonths()} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal: ຄຳນວນຫຼາຍຄົນ */}
      <Modal
        title="ຄຳນວນເງິນເດືອນຫຼາຍຄົນພ້ອມກັນ"
        open={bulkCalculateModalOpen}
        onCancel={() => {
          setBulkCalculateModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={bulkCalculateMutation.isLoading}
        width={600}
        cancelText={"ຍົກເລິກ"}
        okText="ຢືນຢັນ"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleBulkCalculate}
          initialValues={{
            year: currentYear,
            month: currentMonth,
          }}
        >
          <Form.Item
            label="ພະນັກງານ"
            name="userIds"
            rules={[{ required: true, message: "ກະລຸນາເລືອກພະນັກງານ" }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="ເລືອກພະນັກງານຫຼາຍຄົນ"
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={users.map((u: any) => ({
                value: u._id,
                label: `${u.name} (${u.employeeId})`,
              }))}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="ປີ"
                name="year"
                rules={[{ required: true, message: "ກະລຸນາເລືອກປີ" }]}
              >
                <Select options={salaryService.getYears()} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="ເດືອນ"
                name="month"
                rules={[{ required: true, message: "ກະລຸນາເລືອກເດືອນ" }]}
              >
                <Select options={salaryService.getMonths()} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal: ແກ້ໄຂເງິນເດືອນ */}
      <Modal
        title="ແກ້ໄຂເງິນເດືອນ"
        open={updateModalOpen}
        onCancel={() => {
          setUpdateModalOpen(false);
          updateForm.resetFields();
          setSelectedSalary(null);
        }}
        onOk={() => updateForm.submit()}
        confirmLoading={updateMutation.isLoading}
        width={500}
      >
        <Form form={updateForm} layout="vertical" onFinish={handleUpdate}>
          <Form.Item label="ໂບນັດພິເສດ" name="specialBonus">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="ປ້ອນໂບນັດພິເສດ"
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value: any) => value!.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item label="ຈຳນວນຫັກເງິນ" name="deduction">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="ປ້ອນຈຳນວນຫັກເງິນ"
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value: any) => value!.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item label="ໝາຍເຫດ" name="notes">
            <TextArea rows={4} placeholder="ໝາຍເຫດ" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: ລາຍລະອຽດ */}
      <Modal
        title="ລາຍລະອຽດເງິນເດືອນ"
        open={detailModalOpen}
        onCancel={() => {
          setDetailModalOpen(false);
          setSelectedSalary(null);
        }}
        footer={null}
        width={800}
      >
        {selectedSalary && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="ພະນັກງານ" span={2}>
                <strong>{selectedSalary.userId.name}</strong> (
                {selectedSalary.userId.employeeId})
              </Descriptions.Item>

              <Descriptions.Item label="ເດືອນ/ປີ">
                {salaryService.formatMonthYear(
                  selectedSalary.year,
                  selectedSalary.month
                )}
              </Descriptions.Item>

              <Descriptions.Item label="ສະຖານະ">
                <Tag
                  color={salaryService.getStatusColor(selectedSalary.status)}
                >
                  {salaryService.formatStatus(selectedSalary.status)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">ຂໍ້ມູນພື້ນຖານ</Divider>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="ເງິນເດືອນພື້ນຖານ">
                {salaryService.formatCurrency(selectedSalary.baseSalary)}
              </Descriptions.Item>
              <Descriptions.Item label="ວັນທຳງານ/ເດືອນ">
                {selectedSalary.workDaysPerMonth} ວັນ
              </Descriptions.Item>
              <Descriptions.Item label="ເງິນຕໍ່ວັນ">
                {salaryService.formatCurrency(selectedSalary.dailyRate)}
              </Descriptions.Item>
              <Descriptions.Item label="ເງິນຕໍ່ຊົ່ວໂມງ">
                {salaryService.formatCurrency(selectedSalary.hourlyRate)}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">ຂໍ້ມູນການເຂົ້າວຽກ</Divider>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="ວັນທຳງານຕົວຈິງ">
                {selectedSalary.actualWorkDays} ວັນ
              </Descriptions.Item>
              <Descriptions.Item label="ວັນຂາດວຽກ">
                <span style={{ color: "#ff4d4f" }}>
                  {selectedSalary.absentDays} ວັນ
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="ຊົ່ວໂມງເຮັດວຽກ">
                {selectedSalary.totalWorkHours.toFixed(2)} ຊົ່ວໂມງ
              </Descriptions.Item>
              <Descriptions.Item label="ວັນມາສາຍ">
                <span style={{ color: "#faad14" }}>
                  {selectedSalary.lateDays} ວັນ (
                  {selectedSalary.totalLateMinutes} ນາທີ)
                </span>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">ການຄຳນວນເງິນເດືອນ</Divider>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="ເງິນຕາມຊົ່ວໂມງ">
                {salaryService.formatCurrency(selectedSalary.salaryByHours)}
              </Descriptions.Item>
              <Descriptions.Item label="ໂບນັດບໍລິການ">
                <span style={{ color: "#52c41a" }}>
                  {salaryService.formatCurrency(selectedSalary.serviceBonus)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="ໂບນັດພິເສດ">
                <span style={{ color: "#1890ff" }}>
                  {salaryService.formatCurrency(selectedSalary.specialBonus)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="ຈຳນວນຖືກຫັກ">
                <span style={{ color: "#1890ff" }}>
                  {salaryService.formatCurrency(selectedSalary.deduction)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="ເງິນທີ່ໄດ້ຮັບຕົວຈິງ">
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#ff4d4f",
                  }}
                >
                  {salaryService.formatCurrency(selectedSalary.totalSalary)}
                </span>
              </Descriptions.Item>
            </Descriptions>

            {selectedSalary.notes && (
              <>
                <Divider orientation="left">ໝາຍເຫດ</Divider>
                <p>{selectedSalary.notes}</p>
              </>
            )}

            {selectedSalary.approvedBy && (
              <>
                <Divider orientation="left">ຂໍ້ມູນການອະນຸມັດ</Divider>
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="ຜູ້ອະນຸມັດ">
                    {selectedSalary.approvedBy.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="ວັນທີ່ອະນຸມັດ">
                    {dayjs(selectedSalary.approvedAt).format(
                      "DD/MM/YYYY HH:mm"
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {selectedSalary.paidAt && (
              <>
                <Divider orientation="left">ຂໍ້ມູນການຈ່າຍເງິນ</Divider>
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="ວັນທີ່ຈ່າຍເງິນ">
                    {dayjs(selectedSalary.paidAt).format("DD/MM/YYYY HH:mm")}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SalariesPage;
